import { Bell, Menu, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/auth.service';
import StatusBadge from '../components/ui/StatusBadge';

interface TopHeaderProps {
  onOpenMobileNav: () => void;
}

const getRoleLabel = (roleLevel: number | undefined, isOperationManager?: boolean): string => {
  if (roleLevel === -1) return 'Admin';
  if (isOperationManager) return 'Operations Manager';
  if (roleLevel === 0) return 'Manufacturer';
  return 'User';
};

export default function TopHeader({ onOpenMobileNav }: TopHeaderProps) {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();

  const handleLogout = async () => {
    await authService.logout();
    clearAuth();
    navigate('/login');
  };

  const initials = user?.fullName
    ?.split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'U';

  return (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/95 backdrop-blur">
      <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
        <button
          type="button"
          onClick={onOpenMobileNav}
          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 lg:hidden"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" />
        </button>

        <div className="hidden max-w-xl flex-1 items-center sm:flex">
          <label className="relative block w-full">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search modules, batches, manufacturers..."
              aria-label="Global search"
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 outline-none transition focus:border-emerald-300 focus:bg-white focus:ring-2 focus:ring-emerald-100"
            />
          </label>
        </div>

        <div className="ml-auto flex items-center gap-3">
          <StatusBadge tone="anchored" size="sm">
            Blockchain Healthy
          </StatusBadge>

          <button
            type="button"
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
            aria-label="Notifications"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
          </button>

          <div className="hidden items-center gap-2 rounded-xl border border-gray-200 bg-white px-2 py-1.5 sm:flex">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-800">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-semibold text-gray-900">{user?.fullName || 'User'}</p>
              <p className="truncate text-[11px] text-gray-500">
                {getRoleLabel(user?.roleLevel, user?.isOperationManager)}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
}
